import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

/** The All / Matched / Unmatched tabs above a transaction list (Bank page and Transactions page). */
export function TransactionStatusTabs({
  selectedTab,
  onSelectedTabChange,
}: {
  selectedTab: string;
  onSelectedTabChange: (tab: string) => void;
}) {
  const t = useTranslations('consultantTransactions.list.tabs');
  return (
    <Tabs defaultValue="all" className="mb-6" value={selectedTab} onValueChange={onSelectedTabChange}>
      <TabsList>
        <TabsTrigger value="all">{t('all')}</TabsTrigger>
        <TabsTrigger value="matched">{t('matched')}</TabsTrigger>
        <TabsTrigger value="unmatched">{t('unmatched')}</TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

interface TransactionListFooterProps {
  shownCount: number;
  loadedCount: number;
  total: number;
  selectedAccountCount: number;
  selectedTab: string;
  matchedFilter: string;
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
}

/**
 * What sits under a transaction table: how many rows are shown, why there are none, and the
 * server-side page controls (issue #363) — the current page is fetched from the DB.
 */
export function TransactionListFooter({
  shownCount,
  loadedCount,
  total,
  selectedAccountCount,
  selectedTab,
  matchedFilter,
  page,
  totalPages,
  setPage,
}: TransactionListFooterProps) {
  const t = useTranslations('consultantTransactions.list');
  // Matched/unmatched status is derived per-row and only evaluated for the current page (issue #363
  // review follow-up), so an empty result under it doesn't rule out matches on other pages the way
  // the description/amount/date filters do.
  const statusFilter = selectedTab !== 'all' ? selectedTab : matchedFilter;
  const emptyMessage = statusFilter === 'matched' ? t('emptyMatched') : t('emptyUnmatched');

  return (
    <>
      {shownCount > 0 ? (
        <div className="mt-2 text-sm text-muted-foreground">
          {t('showing', { shown: String(shownCount), loaded: String(loadedCount), total: String(total)})}
          {selectedAccountCount > 0 && <> {t('filteredBy', { count: selectedAccountCount })}</>}
        </div>
      ) : (
        <div className="mt-4 text-center text-muted-foreground">
          {statusFilter !== 'all' ? emptyMessage : t('emptyNoMatch')}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{t('page', { page: String(page), totalPages: String(totalPages)})}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('previous')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
            {t('next')}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </>
  );
}
